/**
 * Centralized logging utility with Sentry integration
 *
 * Intelligently sends critical errors to Sentry while filtering out expected errors
 */

import * as Sentry from "@sentry/react";

export interface ErrorContext {
  userId?: string;
  pharmacyId?: string;
  operation?: string; // e.g., "create_dispense", "update_inventory"
  metadata?: Record<string, unknown>;
  skipSentry?: boolean; // Force skip Sentry
}

/**
 * Determine if an error should be sent to Sentry
 */
function shouldSendToSentry(error: unknown, context?: ErrorContext): boolean {
  // Force skip if explicitly requested
  if (context?.skipSentry) {
    return false;
  }

  // Extract error details
  const errorObj = error as {
    code?: string;
    status?: number;
    statusCode?: number;
    message?: string;
  };

  // Don't send validation errors (4xx client errors)
  const httpStatus = errorObj.status ?? errorObj.statusCode;
  if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
    return false;
  }

  // Don't send known error codes (expected business logic)
  const expectedCodes = [
    "VALIDATION_ERROR",
    "INVALID_DATE",
    "DATE_IN_FUTURE",
    "DUPLICATE_RECORD",
    "DUPLICATE_NPI",
    "DUPLICATE_NDC",
    "AUTH_REQUIRED",
    "FORBIDDEN",
    "INVALID_CREDENTIALS",
    "USER_NOT_SELECTED",
    "NOT_FOUND",
    "CONFLICT",
    "BAD_REQUEST",
    "INVALID_FORMAT",
    "INVALID_REFERENCE",
    "PERMISSION_DENIED",
  ];

  if (errorObj.code && expectedCodes.includes(errorObj.code)) {
    return false;
  }

  // Don't send database constraint violations (expected)
  const errorMessage = errorObj.message || String(error);
  if (
    errorMessage.includes("23505") || // Unique violation
    errorMessage.includes("23503") || // Foreign key violation
    errorMessage.includes("duplicate key")
  ) {
    return false;
  }

  // Send everything else to Sentry
  return true;
}

/**
 * Enrich error context for Sentry
 */
function enrichContext(context?: ErrorContext) {
  const enriched: {
    user?: { id?: string; pharmacy_id?: string };
    tags: Record<string, string>;
    extra?: Record<string, unknown>;
  } = {
    tags: {
      environment: import.meta.env.MODE || "development",
    },
  };

  if (context?.userId || context?.pharmacyId) {
    enriched.user = {
      id: context.userId,
      pharmacy_id: context.pharmacyId,
    };
  }

  if (context?.operation) {
    enriched.tags.operation = context.operation;
  }

  if (context?.metadata) {
    enriched.extra = context.metadata;
  }

  return enriched;
}

/**
 * Logger utility
 */
export const logger = {
  /**
   * Log informational message (console only, adds breadcrumb to Sentry)
   */
  info(message: string, meta?: Record<string, unknown>): void {
    console.log(`[INFO] ${message}`, meta || "");

    // Add breadcrumb for context in future errors
    if (typeof window !== "undefined") {
      Sentry.addBreadcrumb({
        category: "info",
        message,
        data: meta,
        level: "info",
      });
    }
  },

  /**
   * Log warning (console + Sentry warning)
   */
  warn(message: string, meta?: Record<string, unknown>): void {
    console.warn(`[WARN] ${message}`, meta || "");

    // Send warnings to Sentry with warning level
    if (typeof window !== "undefined") {
      Sentry.captureMessage(message, {
        level: "warning",
        extra: meta,
      });
    }
  },

  /**
   * Log error (console + conditional Sentry based on error type)
   */
  error(message: string, error: unknown, context?: ErrorContext): void {
    console.error(`[ERROR] ${message}`, error);

    // Only send to Sentry if it's a critical/unexpected error
    if (shouldSendToSentry(error, context)) {
      const enrichedContext = enrichContext(context);

      Sentry.captureException(error, {
        tags: enrichedContext.tags,
        extra: {
          message,
          ...enrichedContext.extra,
        },
        user: enrichedContext.user,
        level: "error",
      });
    }
  },

  /**
   * Log critical error (console + always Sentry)
   */
  critical(message: string, error: unknown, context?: ErrorContext): void {
    console.error(`[CRITICAL] ${message}`, error);

    // Always send critical errors to Sentry
    const enrichedContext = enrichContext(context);

    Sentry.captureException(error, {
      tags: enrichedContext.tags,
      extra: {
        message,
        ...enrichedContext.extra,
      },
      user: enrichedContext.user,
      level: "fatal",
    });
  },

  /**
   * Track user action as breadcrumb for Sentry context
   */
  breadcrumb(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void {
    console.log(`[BREADCRUMB] ${category}: ${message}`, data || "");

    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level: "info",
    });
  },
};

/**
 * Track user actions with breadcrumbs
 * Use this for important user interactions that provide context for errors
 */
export function trackAction(
  action: string,
  category: "ui" | "navigation" | "api",
  data?: Record<string, unknown>
): void {
  logger.breadcrumb(category, action, data);
}
